import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Heart, Home, LogOut, Menu, Search, User, X, LayoutDashboard } from "lucide-react";
import { useAuth, useLang } from "../App";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";

export const dashboardPath = role => ({ admin: "/admin", owner: "/owner", cashier: "/cashier", backoffice: "/backoffice" }[role] || "/account");

const initials = name => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join("") || "?";

export const Logo = ({ light = false, tagline = false }) => (
  <span className="flex flex-col leading-none">
    <span className="text-xl font-bold tracking-tight">
      <span className="text-hs-amber">Habari</span><span className={light ? "text-white" : "text-hs-charcoal"}>Stays</span>
    </span>
    {tagline && <span className="mt-1 hidden text-xs text-hs-muted lg:block">Hotels across Tanzania</span>}
  </span>
);

const LangToggle = ({ className = "" }) => {
  const { lang, toggleLang } = useLang();
  return (
    <button
      type="button"
      onClick={toggleLang}
      aria-label={lang === "sw" ? "Switch to English" : "Badilisha kwa Kiswahili"}
      className={`flex h-9 min-w-[44px] items-center justify-center rounded-full border border-hs-border px-3 text-xs font-semibold text-hs-slate transition-colors hover:border-hs-amber hover:text-hs-amber ${className}`}
      data-testid="lang-toggle"
    >
      {lang === "sw" ? "EN" : "SW"}
    </button>
  );
};

const Navbar = ({ showBottomNav = true }) => {
  const { user, logout } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [pathname, hash]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = event => { if (event.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = previous; window.removeEventListener("keydown", onKey); };
  }, [drawerOpen]);

  const handleLogout = () => {
    logout();
    navigate("/");
    setDrawerOpen(false);
  };

  const isTraveler = !user || user.role === "traveler";
  const accountPath = user ? dashboardPath(user.role) : "/account";
  const browsing = ["/hotels", "/search", "/hotel/"].some(prefix => pathname.startsWith(prefix));
  const links = [
    { to: "/", label: t("home"), active: pathname === "/" && hash !== "#how-it-works" },
    { to: "/hotels", label: lang === "sw" ? "Tafuta Hoteli" : "Browse Hotels", active: browsing },
    { to: "/#how-it-works", label: t("about"), active: pathname === "/" && hash === "#how-it-works" },
  ];
  const tabs = [
    { to: "/", label: t("home"), Icon: Home, active: pathname === "/" },
    { to: "/hotels", label: lang === "sw" ? "Tafuta" : "Search", Icon: Search, active: browsing },
    { to: "/saved", label: lang === "sw" ? "Zilizohifadhiwa" : "Saved", Icon: Heart, active: pathname === "/saved" },
    { to: accountPath, label: lang === "sw" ? "Akaunti" : "Account", Icon: User, active: ["/account", "/dashboard", "/login", "/register"].includes(pathname) },
  ];

  return (
    <>
      <nav className="sticky top-0 z-40 h-16 border-b border-hs-border bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="shrink-0" aria-label="Habari Stays home"><Logo tagline /></Link>

          <div className="hidden items-center gap-8 md:flex">
            {links.map(link => (
              <Link key={link.to} to={link.to} aria-current={link.active ? "page" : undefined} className={`text-sm font-medium transition-colors hover:text-hs-amber ${link.active ? "text-hs-amber" : "text-hs-slate"}`}>
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden items-center gap-3 md:flex">
            <LangToggle />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" aria-label="Account menu" className="flex h-10 w-10 items-center justify-center rounded-full bg-hs-amber-lt text-sm font-bold text-hs-amber ring-offset-2 transition hover:ring-2 hover:ring-hs-amber/40" data-testid="account-menu">
                    {initials(user.full_name || user.email)}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl border-hs-border p-1.5 font-sans">
                  <DropdownMenuLabel className="font-normal">
                    <p className="truncate text-sm font-semibold text-hs-charcoal">{user.full_name}</p>
                    {user.email && <p className="truncate text-xs text-hs-muted">{user.email}</p>}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild className="cursor-pointer rounded-lg py-2">
                    <Link to={accountPath} data-testid="dashboard-link">
                      {isTraveler ? <User className="mr-2 h-4 w-4" /> : <LayoutDashboard className="mr-2 h-4 w-4" />}
                      {isTraveler ? (lang === "sw" ? "Wasifu" : "Profile") : t("dashboard")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer rounded-lg py-2">
                    <Link to="/saved"><Heart className="mr-2 h-4 w-4" />Saved Hotels</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={handleLogout} className="cursor-pointer rounded-lg py-2 text-hs-slate" data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />{lang === "sw" ? "Toka" : "Sign out"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" className="btn-outline px-4 py-2 text-sm" data-testid="login-link">{lang === "sw" ? "Ingia" : "Sign in"}</Link>
                <Link to="/register" className="btn-primary px-4 py-2 text-sm" data-testid="register-link">{t("register")}</Link>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <LangToggle />
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-xl text-hs-charcoal hover:bg-hs-sage"
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile slide-in drawer */}
      <div className={`fixed inset-0 z-50 md:hidden ${drawerOpen ? "" : "pointer-events-none"}`} aria-hidden={!drawerOpen} inert={!drawerOpen}>
        <div className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${drawerOpen ? "opacity-100" : "opacity-0"}`} onClick={() => setDrawerOpen(false)} />
        <div
          role={drawerOpen ? "dialog" : undefined}
          aria-modal={drawerOpen || undefined}
          aria-label="Menu"
          className={`absolute inset-0 flex flex-col bg-white transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          <div className="flex h-16 items-center justify-between border-b border-hs-border px-4">
            <Logo />
            <button type="button" onClick={() => setDrawerOpen(false)} className="flex h-11 w-11 items-center justify-center rounded-xl hover:bg-hs-sage" aria-label="Close menu">
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto px-4 py-6">
            <nav className="space-y-1">
              {[...links, { to: "/saved", label: "Saved Hotels", active: pathname === "/saved" }].map(link => (
                <Link key={link.to} to={link.to} className={`flex min-h-[48px] items-center rounded-xl px-4 text-base font-medium ${link.active ? "bg-hs-amber-lt text-hs-amber" : "text-hs-charcoal hover:bg-hs-sage"}`}>
                  {link.label}
                </Link>
              ))}
              {user && (
                <Link to={accountPath} className="flex min-h-[48px] items-center rounded-xl px-4 text-base font-medium text-hs-charcoal hover:bg-hs-sage">
                  {isTraveler ? "My Account" : t("dashboard")}
                </Link>
              )}
              <Link to="/register-hotel" className="flex min-h-[48px] items-center rounded-xl px-4 text-base font-medium text-hs-charcoal hover:bg-hs-sage" data-testid="list-hotel-btn">
                {t("listHotel")}
              </Link>
            </nav>
            <div className="mt-auto space-y-3 border-t border-hs-border pt-6 pb-safe">
              {user ? (
                <>
                  <p className="px-1 text-sm text-hs-muted">{lang === "sw" ? "Umeingia kama" : "Signed in as"} <span className="font-semibold text-hs-charcoal">{user.full_name}</span></p>
                  <button type="button" onClick={handleLogout} className="btn-outline w-full py-3">{lang === "sw" ? "Toka" : "Sign out"}</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn-outline w-full py-3">{lang === "sw" ? "Ingia" : "Sign in"}</Link>
                  <Link to="/register" className="btn-primary w-full">{t("register")}</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile bottom navigation */}
      {showBottomNav && (
        <nav aria-label="Primary" className="fixed inset-x-0 bottom-0 z-40 border-t border-hs-border bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
          <ul className="grid grid-cols-4">
            {tabs.map(({ to, label, Icon, active }) => (
              <li key={label}>
                <Link to={to} aria-current={active ? "page" : undefined} className={`flex min-h-[56px] flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${active ? "text-hs-amber" : "text-hs-muted"}`}>
                  <Icon className="h-5 w-5" fill={active && Icon === Heart ? "currentColor" : "none"} />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </>
  );
};

export default Navbar;
